import { useEffect } from 'react';
import * as Sentry from '@sentry/react';
import { toast } from 'sonner';
import { MatchEventService } from '@/data/services';
import { i18n } from '@/i18n';
import { HttpError, httpErrorCode, RequestTimeoutError } from '@/services/http';
import { isFetchNetworkError } from '@/lib/networkError';
import { track } from '@/lib/analytics';
import { PHONE_MEDIA_QUERY } from './useIsPhone';
import type { EventAck, MatchEventDto, PendingEvent, PendingUndo, RefusedEvent } from '@/common/model';
import { getActionLabel, getActionName, hasQuality } from '@/utils/actionLabels';
import { useGameStore, type ActionQuality } from '@/store/useGameStore';
import { playerLabel } from '@/features/match-history/model/pointEditRules';
import {
  canRelayPending,
  currentMatchPending,
  currentMatchPendingCount,
  isOwnPending,
  useSyncStore,
} from './useSyncStore';
import {
  isDefinitiveRefusal,
  isRetryableClientStatus,
  READ_ONLY_REFUSAL_CODES,
  requestMatchHistory,
} from './helpers/syncRefusal';
import { teamLockedAsksPayer, teamLockedReactivableAt } from '@/features/pricing/data/planLimitRefusal';
import {
  SYNC_BATCH_DELAY_MS,
  SYNC_BATCH_SIZE,
  SYNC_MAX_RETRIES,
  SYNC_REQUEST_TIMEOUT_MS,
  SYNC_RETRY_DELAYS,
  SYNC_SET_ASIDE_AFTER_MS,
  SYNC_STUCK_RETRY_GAP_MS,
  reconcileScoresFromServer,
} from './helpers/syncReconcile';
import { recordAckDelays, recordQueuedAction, recordResends, sendLiveMatchSummary } from './helpers/liveMatchSummary';

export interface MatchSyncFlush {
  queueEvent: (event: MatchEventDto) => void;
  flushPendingEvents: () => Promise<void>;
}

/**
 * État de synchronisation **partagé par toute l'application**, volontairement
 * au niveau module et non dans un `useRef` ni dans `useSyncStore`.
 *
 * - Pas `useRef` : `useMatchSync` (donc ce hook) est monté plusieurs fois
 *   simultanément pendant un match (tableau de score, undo, événements de jeu),
 *   alors que la file `pendingEvents` vit dans un store Zustand unique. Avec un
 *   verrou par instance, chaque instance se croyait seule et vidait la même
 *   file : chaque action partait autant de fois qu'il y avait d'instances
 *   montées, ce qui triplait le trafic et provoquait la course sur la création
 *   de set côté serveur (audit du 10/09/2026).
 * - Pas `useSyncStore` : ce store est persisté (`volley-sync-storage`). Un
 *   crash ou un rechargement en plein flush y laisserait un verrou pris pour
 *   toujours, et plus rien ne serait jamais envoyé.
 */
let flushTimeout: ReturnType<typeof setTimeout> | null = null;
/**
 * Flush en cours, ou `null`. Sert à la fois de verrou (un seul flush concurrent)
 * et de point de rendez-vous : un appelant qui `await flushPendingEvents()`
 * pendant qu'un flush tourne attend *ce* flush au lieu de repartir aussitôt en
 * croyant, à tort, que la file est vidée.
 */
let inFlightFlush: Promise<void> | null = null;
/** Nombre d'instances du hook montées — voir le nettoyage du timer plus bas. */
let mountedInstances = 0;
/**
 * Whether the live entry screen is shown. The sync store keeps the match left
 * last as its match, so once the coach is back home its refusals must not be
 * reported as the match on screen, with a history button nothing listens to
 * (VOL-208).
 */
let liveScreenMounted = false;

export function setLiveScreenMounted(mounted: boolean): void {
  liveScreenMounted = mounted;
}

/** Warnings speak of « this match » only while its live screen is shown. */
function isShownLive(matchId: string): boolean {
  return liveScreenMounted && useSyncStore.getState().matchId === matchId;
}

/** « Match against Rennes: … », or « Previous match: … » when the opponent is unknown. */
function awayFromMatch(matchId: string, message: string): string {
  const opponent = useSyncStore.getState().matchOpponents[matchId];
  return opponent
    ? i18n.t('match:sync.previous_match_vs', { opponent, message })
    : i18n.t('match:sync.previous_match', { message });
}

/**
 * Matches one of whose batches the server refused for good: their actions go
 * one by one, so a single faulty action no longer holds back the rest of its
 * batch (VOL-208). Cleared once the match has nothing left to send.
 */
const isolatedMatches = new Set<string>();

/**
 * Queued actions of the match the server already refused alone. Read from the
 * refusals already named, not from `refusedEvents`: « Checked, hide » empties
 * that list, and the refused action would then go first again at every pass,
 * holding back the actions entered after it (VOL-208).
 */
function keptIds(matchId: string): Set<string> {
  const { pendingEvents, notifiedRefusedIds } = useSyncStore.getState();
  const named = new Set(notifiedRefusedIds);
  return new Set(
    pendingEvents
      .filter((pe) => pe.matchId === matchId && named.has(pe.event.eventId))
      .map((pe) => pe.event.eventId),
  );
}

/**
 * Actions a pass may send, in entry order. An action that used up its attempts
 * (server erroring, network up) holds back the ones entered after it in its
 * match: sent past it, they reached the server first and its replay played the
 * match out of order (VOL-208). Except an action the server refuses for good, or
 * a match whose refused batch is split: those go last on purpose.
 */
function sendableInOrder(pending: PendingEvent[]): PendingEvent[] {
  const named = new Set(useSyncStore.getState().notifiedRefusedIds);
  const stopped = new Set<string>();
  return pending.filter((pe) => {
    if (pe.matchId === null || !isOwnPending(pe) || stopped.has(pe.matchId)) return false;
    if (pe.retryCount < SYNC_MAX_RETRIES) return true;
    if (!named.has(pe.event.eventId) && !isolatedMatches.has(pe.matchId)) stopped.add(pe.matchId);
    return false;
  });
}

/**
 * The batch a pass sends: actions of a single match, so each action goes to
 * the match it was entered in (VOL-273). The match on screen goes first, so an
 * older match the server keeps refusing never holds it back; otherwise the
 * match of the oldest action. Actions not tied to a server match yet, or
 * entered by another user, are never sent.
 */
function pickBatch(
  pending: PendingEvent[],
  currentMatchId: string | null,
): { matchId: string; events: PendingEvent[] } | null {
  const sendable = sendableInOrder(pending);
  const matchId = sendable.some((pe) => pe.matchId === currentMatchId)
    ? currentMatchId
    : sendable[0]?.matchId;
  if (!matchId) return null;
  const ofMatch = sendable.filter((pe) => pe.matchId === matchId);
  // The actions not refused yet go first: a refused one is retried only once
  // nothing else is left, so the server records everything it accepts, and it
  // never makes a batch of good actions fail with it.
  const kept = keptIds(matchId);
  const fresh = ofMatch.filter((pe) => !kept.has(pe.event.eventId));
  if (fresh.length === 0) return { matchId, events: [ofMatch[0]] };
  if (isolatedMatches.has(matchId)) return { matchId, events: [fresh[0]] };
  return { matchId, events: fresh.slice(0, SYNC_BATCH_SIZE) };
}

/**
 * An action that keeps getting server errors while the server may well record
 * the rest: the first of its match, attempts used up, failing for
 * `SYNC_SET_ASIDE_AFTER_MS`, with an action entered after it to try.
 */
interface StuckAction {
  matchId: string;
  stuck: PendingEvent;
  next: PendingEvent;
}

function findStuckAction(pending: PendingEvent[], currentMatchId: string | null, now: number): StuckAction | null {
  const named = new Set(useSyncStore.getState().notifiedRefusedIds);
  const found: StuckAction[] = [];
  const seen = new Set<string>();
  for (const pe of pending) {
    if (pe.matchId === null || !isOwnPending(pe) || named.has(pe.event.eventId)) continue;
    if (seen.has(pe.matchId)) continue;
    seen.add(pe.matchId);
    const stuckFor = pe.serverErrorSince === undefined ? 0 : now - pe.serverErrorSince;
    if (pe.retryCount < SYNC_MAX_RETRIES || stuckFor < SYNC_SET_ASIDE_AFTER_MS) continue;
    const next = pending.find((other) =>
      other !== pe && other.matchId === pe.matchId && isOwnPending(other) && !named.has(other.event.eventId));
    if (next) found.push({ matchId: pe.matchId, stuck: pe, next });
  }
  return found.find((s) => s.matchId === currentMatchId) ?? found[0] ?? null;
}

function isServerError(error: unknown): boolean {
  return error instanceof HttpError && error.status >= 500;
}

/** Sends one action; true when the server recorded it (or already had it). */
async function sendAlone(matchId: string, pe: PendingEvent): Promise<boolean> {
  useSyncStore.getState().markAttempted([pe.event.eventId]);
  const ack = await MatchEventService.sendEvent(matchId, pe.event, { timeoutMs: SYNC_REQUEST_TIMEOUT_MS });
  if (isFinalAck(ack)) useSyncStore.getState().removePendingEvents([ack.eventId]);
  recordRecorded([ack], [pe], matchId);
  if (ack.deviceConflict) notifyDeviceConflict(matchId);
  const recorded = ack.status === 'accepted' || ack.status === 'duplicate';
  if (recorded && useSyncStore.getState().matchId === matchId
    && ack.sequence > useSyncStore.getState().lastSyncedSequence) {
    useSyncStore.getState().setLastSequence(ack.sequence);
  }
  if (!recorded) notifyRefused(refusedOf([ack], [pe], matchId), isShownLive(matchId));
  return recorded;
}

/**
 * The stuck action is tried once more, then, if the server still errors on it,
 * the action entered after it. The server recording that one shows the error
 * comes from the stuck action, not from a server down: it is set aside, named
 * to the coach, kept on the device and resent last, and the rest of its match
 * goes on. A server down for everything sets nothing aside: the order is kept
 * (VOL-208). Returns whether the pass moved anything.
 */
async function tryPastStuckAction({ matchId, stuck, next }: StuckAction): Promise<boolean> {
  // A failure goes through the ordinary rules: any answer other than a server
  // error (refused for good, read-only team, deleted match) is handled there and
  // clears the lasting server error, so the action is never probed in a loop.
  const tryAlone = async (pe: PendingEvent): Promise<'sent' | 'serverError' | 'failed'> => {
    try {
      await sendAlone(matchId, pe);
      return 'sent';
    } catch (error) {
      handleSendFailure(error, matchId, [pe]);
      return isServerError(error) ? 'serverError' : 'failed';
    }
  };
  // Offline, the pass waits for the network like any other.
  const moved = (outcome: 'sent' | 'failed') => outcome === 'sent' || navigator.onLine;
  const first = await tryAlone(stuck);
  if (first !== 'serverError') return moved(first);
  // Tried twice: a server restarting between the stuck action and the next one
  // would otherwise set aside an action it can record.
  await new Promise((resolve) => setTimeout(resolve, SYNC_STUCK_RETRY_GAP_MS));
  const second = await tryAlone(stuck);
  if (second !== 'serverError') return moved(second);
  const after = await tryAlone(next);
  if (after === 'serverError') return false;
  if (after === 'failed') return moved(after);
  setAside(matchId, stuck);
  return true;
}

function setAside(matchId: string, pe: PendingEvent): void {
  isolatedMatches.add(matchId);
  const shown = isShownLive(matchId);
  const kept: RefusedEvent = {
    event: pe.event,
    matchId,
    userId: pe.userId,
    status: 'kept',
    setAside: true,
    refusedAt: Date.now(),
  };
  if (shown) notifyRefused([kept], true);
  else {
    storeRefused([kept]);
    useSyncStore.getState().markRefusedNotified([pe.event.eventId]);
    notifyHeld(REFUSED_KEPT_TOAST_KEY, matchId, false);
  }
  Sentry.captureMessage('Queued action set aside after server errors', {
    level: 'warning',
    extra: { eventId: pe.event.eventId, matchId, type: pe.event.type, serverErrorSince: pe.serverErrorSince },
  });
}

/**
 * A set-aside action the server finally recorded came after the ones entered
 * later: the coach checks its place in the review, where nothing else may flag
 * it (VOL-208).
 */
function reportRecordedOutOfOrder(acks: EventAck[], matchId: string): void {
  const recorded = new Set(
    acks.filter((a) => a.status === 'accepted').map((a) => a.eventId),
  );
  const late = useSyncStore.getState().refusedEvents.filter((r) => r.setAside && recorded.has(r.event.eventId));
  if (late.length === 0) return;
  late.forEach((r) => {
    Sentry.captureMessage('Set-aside action recorded out of order', {
      level: 'warning',
      extra: { eventId: r.event.eventId, matchId, type: r.event.type },
    });
  });
  const message = i18n.t('match:sync.set_aside_recorded', {
    count: late.length,
    action: describeRefusedEvent(late[0].event),
  });
  toast.warning(isShownLive(matchId) ? message : awayFromMatch(matchId, message), {
    id: `sync-set-aside-recorded-${matchId}`,
    duration: Infinity,
    classNames: SYNC_TOAST_CLASS_NAMES,
  });
}

function hasSendable(pending: PendingEvent[]): boolean {
  return pending.some((pe) => pe.matchId !== null && isOwnPending(pe));
}

/** Failed attempts after which a queued undo is reported as stuck (a few minutes). */
const UNDO_STUCK_REPORT_AFTER = 10;

function ownUndos(undos: PendingUndo[]): PendingUndo[] {
  return undos.filter(isOwnPending);
}

/**
 * Sends the queued undos, oldest first, before any action: an action resent
 * after an edit must reach the server after the undo of its old copy, or the
 * point counts twice there (VOL-251). Returns the matches whose undos are
 * still waiting; their actions wait too.
 */
async function flushUndos(): Promise<Set<string>> {
  const blocked = new Set<string>();
  for (const pu of ownUndos(useSyncStore.getState().pendingUndos)) {
    if (blocked.has(pu.matchId)) continue;
    const onScreen = () => useSyncStore.getState().matchId === pu.matchId;
    try {
      const response = await MatchEventService.undoByEventId(pu.matchId, pu.eventId, {
        signal: AbortSignal.timeout(SYNC_REQUEST_TIMEOUT_MS),
      });
      useSyncStore.getState().removePendingUndos([pu.eventId]);
      clearHeld(pu.matchId);
      clearUndoStuck(pu.matchId);
      if (response.state && onScreen()) {
        useSyncStore.getState().setServerState(response.state);
        useSyncStore.getState().setLastSequence(response.state.lastSequence);
        reconcileScoresFromServer(response.state);
      }
    } catch (error) {
      if (error instanceof HttpError && error.status === 404) {
        // The match was deleted: there is nothing left to undo.
        useSyncStore.getState().removePendingUndos([pu.eventId]);
        clearUndoStuck(pu.matchId);
        continue;
      }
      if (isDefinitiveRefusal(error)) {
        // Sending it again gets the same « no », and the match's actions would wait
        // behind it forever: drop it, the action stays counted (VOL-208).
        useSyncStore.getState().removePendingUndos([pu.eventId]);
        // The refusal toast takes over: it says the action stays counted.
        clearUndoStuck(pu.matchId);
        notifyUndoRefused(pu, isShownLive(pu.matchId));
        Sentry.captureMessage('Queued undo refused', {
          level: 'warning',
          extra: { eventId: pu.eventId, matchId: pu.matchId, error: toSyncErrorMessage(error) },
        });
        continue;
      }
      blocked.add(pu.matchId);
      if (!navigator.onLine) {
        useSyncStore.getState().setStatus('offline');
        // Every other undo would fail the same way.
        ownUndos(useSyncStore.getState().pendingUndos).forEach((u) => blocked.add(u.matchId));
        // Not an attempt: a long outage must neither stretch the retry delay
        // past the network's return nor read as a stuck undo (VOL-324).
        break;
      }
      useSyncStore.getState().updateUndoRetryCount(pu.eventId);
      const attempts = pu.retryCount + 1;
      // The action is gone from the tablet but still counted on the server, and
      // the match's actions wait behind it: the coach must know, once per page
      // load (the count survives a reload, the warning does not).
      if (attempts >= UNDO_STUCK_REPORT_AFTER && !undoStuckNotified.has(pu.matchId)) {
        notifyUndoStuck(pu.matchId, isShownLive(pu.matchId));
      }
      if (attempts === UNDO_STUCK_REPORT_AFTER) {
        // Only server answers go to Sentry: a gym wifi without Internet would
        // otherwise report every long outage.
        if (error instanceof HttpError) {
          Sentry.captureMessage('Queued undo stuck', {
            level: 'warning',
            extra: { eventId: pu.eventId, matchId: pu.matchId, error: toSyncErrorMessage(error) },
          });
        }
      }
      // A read-only team also holds back the match's actions: the coach must learn
      // that neither the undo nor what follows reached the server.
      const heldKey = heldRefusalToastKey(error, isShownLive(pu.matchId));
      if (heldKey) notifyHeld(heldKey, pu.matchId, isShownLive(pu.matchId), error);
      if (onScreen()) {
        setSyncError(error);
        useSyncStore.getState().setStatus('error');
      }
    }
  }
  return blocked;
}

/**
 * Removes an action from the tablet's sync: a queued action never sent is just
 * dropped; one the server may hold gets its undo queued, sent at the next
 * flush, offline and across reloads included (VOL-251).
 */
export function queueUndo(eventId: string, matchId: string): void {
  const store = useSyncStore.getState();
  const queued = store.pendingEvents.find((pe) => pe.event.eventId === eventId);
  if (queued) store.removePendingEvents([eventId]);
  // Undone by the coach: no longer an action the server refuses.
  store.removeRefusedEvents([eventId]);
  if (queued && !queued.attempted) return;
  store.addPendingUndo(eventId, matchId, queued ? describeRefusedEvent(queued.event) : describeLiveEvent(eventId));
  store.rememberQueueContext(matchId, useGameStore.getState().opponentName);
  scheduleFlush();
}

/**
 * A coach signed in on the device sends in their name the actions another
 * account left there, a member removed mid-match being never let back in
 * (VOL-198): one toast per account taken over. `matchIds`: those matches only;
 * `userId`: that account only. Returns how many actions changed hands; the
 * caller sends them.
 */
export function relayOthersPending(scope: { matchIds?: readonly string[]; userId?: string }): number {
  if (!canRelayPending()) return 0;
  const { pendingEvents, pendingUndos } = useSyncStore.getState();
  const others = new Set(
    [...pendingEvents, ...pendingUndos]
      .filter((item) => item.matchId !== null && item.userId !== null && !isOwnPending(item)
        && (scope.matchIds === undefined || scope.matchIds.includes(item.matchId))
        && (scope.userId === undefined || item.userId === scope.userId))
      .map((item) => item.userId as string),
  );
  let total = 0;
  others.forEach((userId) => {
    const count = (scope.matchIds ?? [undefined]).reduce(
      (sum, matchId) => sum + useSyncStore.getState().adoptPending(userId, matchId),
      0,
    );
    if (count === 0) return;
    total += count;
    const name = useSyncStore.getState().userNames[userId];
    toast.info(
      name
        ? i18n.t('dashboard:pending_sync.relayed_named', { count, name })
        : i18n.t('dashboard:pending_sync.relayed', { count }),
      { duration: 6000 },
    );
  });
  return total;
}

/** The undone action as the tablet still holds it, to name its undo if the server refuses it. */
function describeLiveEvent(eventId: string): string | null {
  const game = useGameStore.getState();
  const event = [...game.currentPointEvents, ...game.history].find((e) => e.id === eventId);
  if (!event) return null;
  const opponent = event.isOpponent ? game.teams.them.roster.find((p) => p.name === event.player) : undefined;
  return describeRefusedEvent({
    eventId,
    setNumber: game.currentSet,
    type: event.action,
    quality: event.quality,
    scoreUsSnapshot: event.scoreUs,
    scoreThemSnapshot: event.scoreThem,
    isOpponent: event.isOpponent,
    playerId: event.isOpponent ? undefined : game.teams.us.roster.find((p) => p.name === event.player)?.id,
    opponentPlayerName: opponent?.name ?? (event.isOpponent ? event.player : undefined),
    opponentPlayerNumber: opponent?.number,
  });
}

/**
 * The server answers 200 for every action of a batch: an ack the queue does
 * not drop would be sent again forever (VOL-274).
 */
const FINAL_ACK_STATUSES: ReadonlySet<EventAck['status']> = new Set([
  'accepted',
  'duplicate',
  'rejected',
  'ignored',
]);

function isFinalAck(ack: EventAck): boolean {
  return FINAL_ACK_STATUSES.has(ack.status);
}

/**
 * Feeds the match summary with the actions the server recorded, and sends it
 * once the match end is recorded: the summary then counts every action
 * confirmed before it (VOL-208).
 */
function recordRecorded(acks: EventAck[], sent: PendingEvent[], matchId: string): void {
  const recorded = new Set(
    acks.filter((a) => a.status === 'accepted' || a.status === 'duplicate').map((a) => a.eventId),
  );
  const done = sent.filter((pe) => recorded.has(pe.event.eventId));
  const now = Date.now();
  recordAckDelays(
    matchId,
    done.filter((pe) => !STRUCTURAL_TYPES.has(pe.event.type)).map((pe) => now - pe.createdAt),
  );
  if (done.some((pe) => pe.event.type === 'MATCH_END')) sendLiveMatchSummary(matchId);
}

/** `current` for the match on screen, `previous:<matchId>` for a match left earlier. */
type RefusedScope = 'current' | `previous:${string}`;

function refusedScope(matchId: string, onScreen: boolean): RefusedScope {
  return onScreen ? 'current' : `previous:${matchId}`;
}

/** Persistent sync warnings: a close button big enough for a finger (VOL-208). */
const SYNC_TOAST_CLASS_NAMES = {
  // The visible dot keeps its size, over the title it would hide; only the area
  // a finger can hit grows to 44 px.
  closeButton: "after:absolute after:-inset-2.5 after:content-['']",
  actionButton: '!h-11 !px-3 !text-sm',
};

export const SUPPORT_MAILTO = 'mailto:contact@volleystatspro.com';

/** A refusal for good only lifts with help: the warning offers to write to support. */
function supportAction() {
  return {
    label: i18n.t('match:sync.contact_support'),
    onClick: () => window.open(SUPPORT_MAILTO, '_self'),
  };
}

/**
 * Refusals the coach has not dismissed yet, per toast: the persistent toast is
 * updated with the running total instead of stacking one per send.
 */
const refusedSinceDismiss = new Map<RefusedScope, RefusedEvent[]>();

/** Who made the action, from the live rosters: « #7 Enzo », or `null`. */
function refusedPlayer(event: MatchEventDto): string | null {
  if (event.isOpponent) {
    const name = event.opponentPlayerName ?? (event.opponentPlayerNumber != null ? `#${event.opponentPlayerNumber}` : null);
    return name ? playerLabel(name, event.opponentPlayerNumber) : null;
  }
  if (!event.playerId) return null;
  const player = useGameStore.getState().teams.us.roster.find((p) => p.id === event.playerId);
  return player ? playerLabel(player.name, player.number) : null;
}

/**
 * « Set 2 · 12-10 · Kill (#7 Enzo) »: enough to find the action again and enter
 * it anew (VOL-208).
 */
export function describeRefusedEvent(event: MatchEventDto): string {
  // The quality label alone (« Kill », « Parfait ») does not say which action it was.
  const name = hasQuality(event.type)
    ? `${getActionName(event.type)} ${getActionLabel(event.type, event.quality as ActionQuality)}`
    : getActionName(event.type);
  const who = refusedPlayer(event);
  const action = who ? `${name} (${who})` : name;
  return event.scoreUsSnapshot != null && event.scoreThemSnapshot != null
    ? i18n.t('match:sync.refused_action_score', {
      set: event.setNumber,
      score: `${event.scoreUsSnapshot}-${event.scoreThemSnapshot}`,
      action,
    })
    : i18n.t('match:sync.refused_action', { set: event.setNumber, action });
}

/** Refused actions of a batch, with what they were, so the coach can find them. */
function refusedOf(acks: EventAck[], sent: PendingEvent[], matchId: string): RefusedEvent[] {
  const byId = new Map(sent.map((pe) => [pe.event.eventId, pe]));
  const refusedAt = Date.now();
  return acks.flatMap((ack): RefusedEvent[] => {
    const pe = byId.get(ack.eventId);
    return pe && (ack.status === 'rejected' || ack.status === 'ignored')
      ? [{ event: pe.event, matchId, userId: pe.userId, status: ack.status, refusedAt }]
      : [];
  });
}

/** A batch refused for good by an HTTP error: still queued, named all the same. */
function keptOf(sent: PendingEvent[], matchId: string): RefusedEvent[] {
  const refusedAt = Date.now();
  return sent.map((pe) => ({ event: pe.event, matchId, userId: pe.userId, status: 'kept' as const, refusedAt }));
}

/** Rows that are not gestures: re-entering them makes no sense, the review fixes them. */
const STRUCTURAL_TYPES: ReadonlySet<string> = new Set(['SET_START', 'MATCH_END', 'SKIP_TO_SCORE']);

function refusedHintKey(refused: RefusedEvent[]): string {
  // A set start or match end set aside: re-entering it makes no sense either,
  // the sets are checked in the review.
  if (refused.some((r) => r.setAside)) {
    return refused.every((r) => STRUCTURAL_TYPES.has(r.event.type))
      ? 'match:sync.refused_hint_structural'
      : 'match:sync.refused_hint_set_aside';
  }
  if (refused.some((r) => r.status === 'kept')) return 'match:sync.refused_hint_kept';
  if (refused.some((r) => r.status === 'ignored')) return 'match:sync.refused_hint_ignored';
  if (refused.every((r) => STRUCTURAL_TYPES.has(r.event.type))) return 'match:sync.refused_hint_structural';
  return 'match:sync.refused_hint';
}

/**
 * Actions already named to the coach: a kept batch is refused again at every
 * retry and must not bring the warning back each time, reloads included.
 */
function notifiedRefusedIds(): Set<string> {
  return new Set(useSyncStore.getState().notifiedRefusedIds);
}

/** Short title of the refusal warning, `null` when nothing was refused. */
function refusedTitle(refused: RefusedEvent[]): string | null {
  return refused.length > 0 ? i18n.t('match:sync.refused_title', { count: refused.length }) : null;
}

const SHOWN_REFUSED = 2;

function refusedDescription(refused: RefusedEvent[]): string {
  // Newest first, like the history panel.
  const shown = refused.slice(-SHOWN_REFUSED).reverse().map((r) => describeRefusedEvent(r.event));
  const more = refused.length - shown.length;
  const joined = shown.join(i18n.t('match:sync.refused_separator'));
  const list = more > 0 ? `${joined} ${i18n.t('match:sync.refused_more', { count: more })}` : joined;
  return i18n.t('match:sync.refused_description', {
    list,
    hint: i18n.t(refusedHintKey(refused), { count: refused.length }),
  });
}

/**
 * Refusals to keep for the history panel. A kept action already named is
 * refused again at every retry: adding it back would undo « Checked, hide ».
 */
function storeRefused(refused: RefusedEvent[]): void {
  const notified = notifiedRefusedIds();
  useSyncStore.getState().addRefusedEvents(
    refused.filter((r) => r.status !== 'kept' || !notified.has(r.event.eventId)),
  );
}

/**
 * The coach must learn that actions shown on the tablet were not recorded,
 * and which ones, including those of a match left earlier whose queue is sent
 * in the background. The server history never shows them, so they are kept
 * for the history panel. Returns this batch's title, `null` if nothing was
 * refused.
 */
function notifyRefused(refused: RefusedEvent[], onScreen: boolean): string | null {
  const title = refusedTitle(refused);
  if (!title) return null;
  storeRefused(refused);
  const notified = notifiedRefusedIds();
  const fresh = refused.filter((r) => !notified.has(r.event.eventId));
  if (fresh.length === 0) return title;
  useSyncStore.getState().markRefusedNotified(fresh.map((r) => r.event.eventId));

  const { matchId } = fresh[0];
  const scope = refusedScope(matchId, onScreen);
  const all = [...(refusedSinceDismiss.get(scope) ?? []), ...fresh];
  refusedSinceDismiss.set(scope, all);
  const totalTitle = refusedTitle(all) ?? title;
  const kept = all.some((r) => r.status === 'kept');
  // Kept until dismissed: the coach watches the rally, not the tablet.
  toast.warning(onScreen ? totalTitle : awayFromMatch(matchId, totalTitle), {
    id: `sync-refused-${scope}`,
    description: refusedDescription(all),
    duration: Infinity,
    classNames: SYNC_TOAST_CLASS_NAMES,
    // Only the match on screen has its history at hand; elsewhere, support is
    // the way out of a refusal for good.
    action: onScreen
      ? { label: i18n.t('match:sync.see_history'), onClick: requestMatchHistory }
      : kept ? supportAction() : undefined,
    onDismiss: () => {
      refusedSinceDismiss.delete(scope);
    },
  });
  return title;
}

/** Matches whose two-device warning was shown in this page load. */
const deviceConflictNotified = new Set<string>();

/**
 * Another device entered actions of this match after the ones just sent: they
 * are recorded, but filed after it (VOL-14). Said once per match: nothing is
 * blocked, the coach checks the order in the history.
 */
function notifyDeviceConflict(matchId: string): void {
  if (deviceConflictNotified.has(matchId)) return;
  deviceConflictNotified.add(matchId);
  track('live_device_conflict_detected', { match_id: matchId });
  const shown = isShownLive(matchId);
  const message = i18n.t('match:sync.deviceConflict');
  toast.warning(shown ? message : awayFromMatch(matchId, message), {
    id: `sync-device-conflict-${matchId}`,
    duration: Infinity,
    classNames: SYNC_TOAST_CLASS_NAMES,
    action: shown ? { label: i18n.t('match:sync.see_history'), onClick: requestMatchHistory } : undefined,
  });
}

/** Undos the server refused for good, since the coach last closed the warning. */
const undoRefusedSinceDismiss = new Map<RefusedScope, PendingUndo[]>();

function notifyUndoRefused(undo: PendingUndo, onScreen: boolean): void {
  const scope = refusedScope(undo.matchId, onScreen);
  const all = [...(undoRefusedSinceDismiss.get(scope) ?? []), undo];
  undoRefusedSinceDismiss.set(scope, all);
  // One undo with a known action is named, the action staying counted is the
  // part the coach must check.
  const message = all.length === 1 && undo.label
    ? i18n.t('match:sync.undo_refused_named', { action: undo.label })
    : i18n.t('match:sync.undo_refused', { count: all.length });
  toast.warning(onScreen ? message : awayFromMatch(undo.matchId, message), {
    id: `sync-undo-refused-${scope}`,
    duration: Infinity,
    classNames: SYNC_TOAST_CLASS_NAMES,
    onDismiss: () => {
      undoRefusedSinceDismiss.delete(scope);
    },
  });
}

/** Matches warned of an undo that keeps failing, until one of its undos goes through. */
const undoStuckNotified = new Set<string>();
const undoStuckToastId = (matchId: string) => `sync-undo-stuck-${matchId}`;

function notifyUndoStuck(matchId: string, onScreen: boolean): void {
  undoStuckNotified.add(matchId);
  const message = i18n.t('match:sync.undo_stuck');
  toast.warning(onScreen ? message : awayFromMatch(matchId, message), {
    id: undoStuckToastId(matchId),
    duration: Infinity,
    classNames: SYNC_TOAST_CLASS_NAMES,
  });
}

function clearUndoStuck(matchId: string): void {
  if (!undoStuckNotified.delete(matchId)) return;
  toast.dismiss(undoStuckToastId(matchId));
}

/**
 * Refusals that keep the actions on the device until the team is writable
 * again: retrying every few seconds cannot help, and the coach must know the
 * actions are not on the server yet.
 */
const READ_ONLY_TOAST_KEYS: Readonly<Record<string, string>> = {
  TEAM_LOCKED: 'match:sync.teamLockedToast',
  TEAM_PAST_SEASON_READ_ONLY: 'match:sync.teamPastSeasonToast',
  // Removed from the team (or the club) during the match: the actions stay
  // queued for this account until a coach adds it back (VOL-198).
  MATCH_ACCESS_DENIED: 'match:sync.accessRevokedToast',
};

const REFUSED_KEPT_TOAST_KEY = 'match:sync.refusedKeptToast';

/**
 * On a phone the long version covers the scoreboard: the short one keeps the
 * order to stop, and the sync indicator says whom to ask.
 */
const PHONE_TOAST_KEYS: Record<string, string> = {
  'match:sync.accessRevokedToast': 'match:sync.accessRevokedToastShort',
  'match:sync.teamLockedUntilToast': 'match:sync.teamLockedUntilToastShort',
};

function phoneToastKey(messageKey: string): string {
  const short = PHONE_TOAST_KEYS[messageKey];
  if (!short || typeof window === 'undefined' || typeof window.matchMedia !== 'function') return messageKey;
  return window.matchMedia(PHONE_MEDIA_QUERY).matches ? short : messageKey;
}

/**
 * Warning for a refusal that holds a match's queue on the device, `null` for an
 * ordinary failure. A match left earlier and refused for good also gets one:
 * nothing else on screen would ever say its actions are not on the server
 * (VOL-208). On screen, the sync indicator already shows the error.
 */
function heldRefusalToastKey(error: unknown, onScreen: boolean): string | null {
  const code = httpErrorCode(error);
  // « Reactivate it » would send a coach who cannot to a dialog that refuses him (VOL-199).
  if (teamLockedAsksPayer(error)) return 'match:sync.teamLockedAskPayerToast';
  // Right after a swap the active teams dialog refuses the reactivation for 7 days.
  if (teamLockedReactivableAt(error)) return 'match:sync.teamLockedUntilToast';
  if (code && READ_ONLY_REFUSAL_CODES.has(code)) return READ_ONLY_TOAST_KEYS[code];
  return !onScreen && isDefinitiveRefusal(error) ? REFUSED_KEPT_TOAST_KEY : null;
}

/**
 * End of the 7-day delay holding a locked team, written for the warnings
 * (VOL-199). Month in letters: « 11/07 » reads as July or November depending on
 * the language; within 7 days the year adds nothing.
 */
function reactivableDate(error: unknown): string | undefined {
  const at = teamLockedReactivableAt(error);
  return at?.toLocaleString(i18n.language, { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' });
}

// One toast per match: a send that goes through for another match must not
// clear the warning of the match still refused.
const heldToastId = (matchId: string) => `sync-team-read-only-${matchId}`;
/** Matches whose holding refusal is still going on. */
const heldRefused = new Set<string>();
/**
 * Matches whose warning the coach closed: not shown again while the refusal
 * lasts, or it would come back over the scoreboard at every rally. The sync
 * indicator keeps saying it.
 */
const heldDismissed = new Set<string>();

function notifyHeld(messageKey: string, matchId: string, onScreen: boolean, error?: unknown): void {
  heldRefused.add(matchId);
  useSyncStore.getState().setMatchHeld(matchId, messageKey === REFUSED_KEPT_TOAST_KEY ? 'refused' : 'readOnly');
  if (heldDismissed.has(matchId)) return;
  // Undos count too: one may be all that waits for the match.
  const count = currentMatchPendingCount({ ...useSyncStore.getState(), matchId });
  const message = i18n.t(phoneToastKey(messageKey), { count, date: reactivableDate(error) });
  // Kept until dismissed or until the queue goes through: the coach keeps
  // entering the rally and must not believe the actions are saved.
  toast.warning(onScreen ? message : awayFromMatch(matchId, message), {
    id: heldToastId(matchId),
    duration: Infinity,
    classNames: SYNC_TOAST_CLASS_NAMES,
    action: messageKey === REFUSED_KEPT_TOAST_KEY ? supportAction() : undefined,
    onDismiss: () => {
      // Also fired by the dismissal on success: only a refusal still going on counts.
      if (heldRefused.has(matchId)) heldDismissed.add(matchId);
    },
  });
}

function clearHeld(matchId: string): void {
  useSyncStore.getState().setMatchHeld(matchId, null);
  if (!heldRefused.has(matchId)) return;
  heldRefused.delete(matchId);
  heldDismissed.delete(matchId);
  toast.dismiss(heldToastId(matchId));
}

/**
 * Next pass for undos still waiting, backed off on their failures. The match on
 * screen sets the pace: an older match refused in a loop must not slow it down.
 */
function scheduleUndoRetry(): void {
  const { pendingUndos, matchId } = useSyncStore.getState();
  const waiting = ownUndos(pendingUndos);
  if (waiting.length === 0) return;
  const onScreen = waiting.filter((pu) => pu.matchId === matchId);
  const basis = onScreen.length > 0 ? onScreen : waiting;
  const maxRetry = Math.max(0, ...basis.map((pu) => pu.retryCount));
  scheduleFlush(SYNC_RETRY_DELAYS[Math.min(maxRetry, SYNC_RETRY_DELAYS.length - 1)]);
}

/**
 * A send that failed: purge, isolate, name or count it. Returns whether a refusal
 * now holds the match's queue (read-only team, refusal for good).
 */
function handleSendFailure(error: unknown, matchId: string, eventsToSend: PendingEvent[]): boolean {
  // Not shown live, rather than not the store's match: the store keeps the match
  // left last, and a match deleted after « Quitter » would retry forever (VOL-208).
  if (!isShownLive(matchId) && error instanceof HttpError && error.status === 404) {
    // A match deleted since: its actions can go nowhere, keeping them would
    // retry forever on every tablet session.
    useSyncStore.getState().removePendingForMatch(matchId);
    return false;
  }
  if (!navigator.onLine) {
    useSyncStore.getState().setStatus('offline');
    return false;
  }
  if (isDefinitiveRefusal(error) && eventsToSend.length > 1) {
    // One faulty action refuses the whole batch: resend one by one before
    // naming anything, so only that action stays on the device (VOL-208).
    isolatedMatches.add(matchId);
    return false;
  }
  const shown = isShownLive(matchId);
  const heldKey = heldRefusalToastKey(error, shown);
  if (heldKey) notifyHeld(heldKey, matchId, shown, error);
  if (isDefinitiveRefusal(error)) {
    // Kept on the device, but named: the coach must know which actions the
    // server refuses, on screen as in the history (VOL-208).
    useSyncStore.getState().setMatchHeld(matchId, 'refused');
    const kept = keptOf(eventsToSend, matchId);
    if (shown) notifyRefused(kept, true);
    else {
      storeRefused(kept);
      useSyncStore.getState().markRefusedNotified(kept.map((r) => r.event.eventId));
    }
  }
  if (useSyncStore.getState().matchId === matchId) {
    setSyncError(error);
    useSyncStore.getState().setStatus('error');
  }
  // Once per action: copies of one action in the batch used up its attempts
  // as many times faster (VOL-208).
  const failed = [...new Set(eventsToSend.map((pe) => pe.event.eventId))];
  // Only a server error counts towards setting an action aside; any other answer
  // gives it back to the rules above (VOL-208).
  if (isServerError(error)) useSyncStore.getState().markServerError(failed, Date.now());
  else useSyncStore.getState().clearServerError(failed);
  failed.forEach((eventId) => {
    useSyncStore.getState().updateRetryCount(eventId);
  });
  recordResends(matchId, failed.length);
  return heldKey !== null;
}

async function runFlush(): Promise<void> {
  const initial = useSyncStore.getState();

  if (!hasSendable(initial.pendingEvents) && ownUndos(initial.pendingUndos).length === 0) return;
  if (!navigator.onLine) {
    initial.setStatus('offline');
    return;
  }

  const blocked = await flushUndos();
  const sendableNow = (pending: PendingEvent[]) =>
    pending.filter((pe) => pe.matchId === null || !blocked.has(pe.matchId));
  const store = useSyncStore.getState();
  if (!hasSendable(sendableNow(store.pendingEvents))) {
    // Only undos went: clear an error they left, unless one of the match is still refused.
    if (store.status === 'error' && store.matchId !== null && !blocked.has(store.matchId)) {
      store.setStatus('idle');
    }
    scheduleUndoRetry();
    return;
  }

  const stuck = findStuckAction(sendableNow(store.pendingEvents), store.matchId, Date.now());
  // Another match's stuck action must not delay the match on screen.
  if (stuck && (stuck.matchId === store.matchId || currentMatchPending(store).length === 0)) {
    const moved = await tryPastStuckAction(stuck);
    scheduleFlush(moved ? SYNC_BATCH_DELAY_MS : SYNC_RETRY_DELAYS[SYNC_RETRY_DELAYS.length - 1]);
    return;
  }

  // The match on screen has nothing left to send (its attempts are used up, or an
  // action that used them up holds the others back): start them over before
  // anything else, or another match's actions would keep it from ever going.
  const current = currentMatchPending(store);
  const currentSendable = sendableInOrder(store.pendingEvents).some((pe) => pe.matchId === store.matchId);
  if (current.length > 0 && !currentSendable) {
    useSyncStore.setState({
      pendingEvents: store.pendingEvents.map((pe) =>
        current.includes(pe) ? { ...pe, retryCount: 0 } : pe,
      ),
    });
  }

  let batch = pickBatch(sendableNow(useSyncStore.getState().pendingEvents), store.matchId);
  if (!batch) {
    useSyncStore.setState({
      pendingEvents: store.pendingEvents.map((pe) => ({ ...pe, retryCount: 0 })),
    });
    batch = pickBatch(sendableNow(useSyncStore.getState().pendingEvents), store.matchId);
  }
  if (!batch) {
    scheduleUndoRetry();
    return;
  }

  const { matchId, events: eventsToSend } = batch;
  // The sync indicator, error and server state belong to the match on screen:
  // another match's send must not show its error there (VOL-273).
  const isCurrentMatch = () => useSyncStore.getState().matchId === matchId;
  if (isCurrentMatch()) store.setStatus('syncing');

  // A send the network never answers would hold the flush lock, and every
  // later action behind it, for good. The server dedups on eventId, so a retry
  // after a timeout cannot count an action twice.
  const sendOptions = { timeoutMs: SYNC_REQUEST_TIMEOUT_MS };
  // Flagged before the send, not on failure: a batch recorded just before the
  // wifi drops fails as « offline », and undoing it must still reach the server.
  useSyncStore.getState().markAttempted(eventsToSend.map((pe) => pe.event.eventId));

  let acks: EventAck[] = [];
  let heldRefusal = false;
  try {
    if (eventsToSend.length === 1) {
      const ack = await MatchEventService.sendEvent(matchId, eventsToSend[0].event, sendOptions);
      acks = [ack];
      if (isFinalAck(ack)) {
        useSyncStore.getState().removePendingEvents([ack.eventId]);
      }
      if (ack.deviceConflict) notifyDeviceConflict(matchId);
      if (
        (ack.status === 'accepted' || ack.status === 'duplicate')
        && isCurrentMatch()
        && ack.sequence > useSyncStore.getState().lastSyncedSequence
      ) {
        useSyncStore.getState().setLastSequence(ack.sequence);
      }
    } else {
      const response = await MatchEventService.sendEvents(
        matchId,
        eventsToSend.map((pe) => pe.event),
        sendOptions,
      );
      acks = response.acks;
      useSyncStore.getState().removePendingEvents(acks.filter(isFinalAck).map((a) => a.eventId));
      if (response.deviceConflict) notifyDeviceConflict(matchId);

      if (response.state && isCurrentMatch()) {
        useSyncStore.getState().setServerState(response.state);
        useSyncStore.getState().setLastSequence(response.state.lastSequence);
        reconcileScoresFromServer(response.state);
      }
    }

    clearHeld(matchId);
    recordRecorded(acks, eventsToSend, matchId);
    reportRecordedOutOfOrder(acks, matchId);
    // Kept actions that finally went through are no longer « not recorded ».
    useSyncStore.getState().removeRefusedEvents(
      acks.filter((a) => a.status === 'accepted' || a.status === 'duplicate').map((a) => a.eventId),
    );
    const refused = notifyRefused(refusedOf(acks, eventsToSend, matchId), isShownLive(matchId));
    if (isCurrentMatch()) {
      useSyncStore.getState().setError(refused);
      useSyncStore.getState().setStatus('idle');
    }
  } catch (error) {
    heldRefusal = handleSendFailure(error, matchId, eventsToSend);
  }

  const { pendingEvents: remaining, matchId: currentMatchId } = useSyncStore.getState();
  if (!remaining.some((pe) => pe.matchId === matchId)) isolatedMatches.delete(matchId);
  // Back-off follows the batch that goes next, not another match's failures.
  const next = pickBatch(remaining, currentMatchId);
  if (next || hasSendable(remaining)) {
    const onScreen = currentMatchPending({ matchId: currentMatchId, pendingEvents: remaining });
    const basis = onScreen.length > 0 ? onScreen : next?.events;
    const basisMatchId = onScreen.length > 0 ? currentMatchId : next?.matchId;
    // While a refused batch is split, the actions not tried alone yet must not
    // wait the long step of the one just refused.
    const kept = keptIds(matchId);
    const untriedLeft = isolatedMatches.has(matchId)
      && remaining.some((pe) => pe.matchId === matchId && !kept.has(pe.event.eventId));
    // Nothing sendable left means every attempt was used, and a read-only team
    // or a refusal for good only lifts by hand: keep the longest step, for that
    // match only.
    const maxRetry = basis && !(heldRefusal && basisMatchId === matchId && !untriedLeft)
      ? Math.max(0, ...basis.map((pe) => pe.retryCount))
      : SYNC_RETRY_DELAYS.length - 1;
    const delay = SYNC_RETRY_DELAYS[Math.min(maxRetry, SYNC_RETRY_DELAYS.length - 1)];
    scheduleFlush(delay);
  } else {
    scheduleUndoRetry();
  }
}

/**
 * Message d'erreur lisible pour l'indicateur de synchro. `HttpService` remonte
 * des messages bruts (« Failed to fetch », « HTTP 500 … », parfois un corps
 * JSON entier) que le coach ne peut pas interpréter en plein match : on ramène
 * les cas courants à une phrase actionnable et on tronque le reste.
 */
export function toSyncErrorMessage(error: unknown): string {
  // A read-only team is not an expired session: the actions stay queued on the
  // device, the coach must reactivate the team rather than sign in again.
  const code = httpErrorCode(error);
  if (code === 'TEAM_LOCKED') {
    if (teamLockedAsksPayer(error)) return i18n.t('match:sync.teamLockedAskPayer');
    const date = reactivableDate(error);
    return date ? i18n.t('match:sync.teamLockedUntil', { date }) : i18n.t('match:sync.teamLocked');
  }
  if (code === 'TEAM_PAST_SEASON_READ_ONLY') return i18n.t('match:sync.teamPastSeason');
  if (code === 'MATCH_ACCESS_DENIED') return i18n.t('match:sync.accessRevoked');
  if (error instanceof RequestTimeoutError) return i18n.t('match:sync.timeout');
  // The status first: a NestJS 500 says « Internal server error » and a 429
  // « ThrottlerException », neither of which the text rules below recognise.
  if (error instanceof HttpError) {
    if (error.status >= 500) return i18n.t('match:sync.serverError');
    if (isRetryableClientStatus(error.status)) return i18n.t('match:sync.busy');
  }

  const raw = error instanceof Error ? error.message : typeof error === 'string' ? error : '';
  const message = raw.trim();

  if (!message) return i18n.t('match:sync.failed');
  if (isFetchNetworkError(error) || /failed to fetch|networkerror|network request failed|load failed/i.test(message)) {
    return i18n.t('match:sync.unreachable');
  }
  if (/\b(401|403)\b|unauthorized|forbidden/i.test(message)) {
    return i18n.t('match:sync.sessionExpired');
  }
  if (/\b5\d{2}\b/.test(message)) return i18n.t('match:sync.serverError');
  if (/timeout|timed out|aborted/i.test(message)) return i18n.t('match:sync.timeout');

  return message.length > 80 ? `${message.slice(0, 79)}…` : message;
}

/**
 * Errors the queue gets over by itself (no answer, unreachable, server down,
 * « not now »): the indicator shows a retry, not an error to act on (VOL-324).
 */
export function isTransientSyncError(error: unknown): boolean {
  if (error instanceof RequestTimeoutError || isFetchNetworkError(error)) return true;
  if (error instanceof HttpError) return error.status >= 500 || isRetryableClientStatus(error.status);
  if (!(error instanceof Error)) return false;
  return error.message === SYNC_IN_FLIGHT_TIMEOUT_ERROR || error.name === 'TimeoutError';
}

/** The sync indicator's message for a failed send or undo, flagged when it clears up by itself. */
export function setSyncError(error: unknown): void {
  useSyncStore.getState().setError(toSyncErrorMessage(error), { retrying: isTransientSyncError(error) });
}

/**
 * Vide la file. Un seul flush à la fois pour toute l'application : si un flush
 * tourne déjà, on renvoie sa promesse au lieu d'en lancer un second.
 */
export function flushPendingEvents(): Promise<void> {
  if (inFlightFlush) return inFlightFlush;

  const pending = runFlush().finally(() => {
    inFlightFlush = null;
  });
  inFlightFlush = pending;
  return pending;
}

/**
 * Resolves once the send in progress, if any, is over. Undoing an event still
 * in the queue just drops it locally: while it is being sent, that would let
 * the request reach the server anyway and the event count twice (VOL-221).
 */
export const SYNC_IN_FLIGHT_TIMEOUT_ERROR = 'sync_in_flight_timeout';

export async function waitForInFlightFlush(timeoutMs?: number): Promise<void> {
  if (!inFlightFlush) return;
  if (timeoutMs === undefined) {
    await inFlightFlush;
    return;
  }
  // A send a stalled network never answers must not hold the caller forever.
  let timer: ReturnType<typeof setTimeout> | undefined;
  const timeout = new Promise<never>((_, reject) => {
    timer = setTimeout(() => reject(new Error(SYNC_IN_FLIGHT_TIMEOUT_ERROR)), timeoutMs);
  });
  try {
    await Promise.race([inFlightFlush, timeout]);
  } finally {
    clearTimeout(timer);
  }
}

/**
 * Vide la file **entièrement**, et pas seulement le premier lot.
 *
 * `runFlush` n'envoie que `SYNC_BATCH_SIZE` événements par passe : un simple
 * `await flushPendingEvents()` avant de quitter un match laisserait donc tout
 * ce qui dépasse le lot dans la file. On répète tant que la file diminue, et on
 * s'arrête dès qu'une passe n'a rien fait avancer (serveur injoignable, ou
 * événements ayant épuisé leurs tentatives) pour ne pas boucler indéfiniment.
 *
 * Retourne le nombre d'événements restants — `0` signifie « tout est parti ».
 */
export async function drainPendingEvents(maxRounds = 20): Promise<number> {
  // Only the match on screen counts: actions another match keeps failing must
  // neither stop its drain nor show up in its leftover count (VOL-273).
  const currentCount = () => currentMatchPendingCount(useSyncStore.getState());
  // A background send of another match already running would count as a round
  // that moved nothing, and stop the drain before this match's queue went.
  await waitForInFlightFlush().catch(() => undefined);
  let remaining = currentCount();

  for (let round = 0; round < maxRounds && remaining > 0; round += 1) {
    await flushPendingEvents();
    const after = currentCount();
    if (after >= remaining) return after;
    remaining = after;
  }

  return remaining;
}

export function scheduleFlush(delay = SYNC_BATCH_DELAY_MS): void {
  if (flushTimeout) clearTimeout(flushTimeout);
  flushTimeout = setTimeout(() => {
    flushTimeout = null;
    flushPendingEvents();
  }, delay);
}

export function queueEvent(event: MatchEventDto): void {
  const store = useSyncStore.getState();
  store.addPendingEvent(event);
  store.rememberQueueContext(store.matchId, useGameStore.getState().opponentName);
  if (!STRUCTURAL_TYPES.has(event.type)) {
    recordQueuedAction(store.matchId, currentMatchPendingCount(useSyncStore.getState()));
  }
  scheduleFlush();
}

/**
 * The native app came back to the foreground, or the network came back (the
 * `online` listener). A suspended webview may have missed the `online` event,
 * and its timers were frozen: send what is queued. Leaves alone a send already running or
 * planned (lock, retry back-off) and an error whose retry logic owns the queue.
 */
export function resumeSyncOnForeground(): void {
  // Nothing sends the queue while signed out (BackgroundSync not mounted).
  if (mountedInstances === 0 || !navigator.onLine) return;
  const store = useSyncStore.getState();
  if (store.status === 'offline') {
    store.setStatus('idle');
    void flushPendingEvents();
    return;
  }
  if (store.status !== 'idle' || flushTimeout || inFlightFlush) return;
  if (store.pendingEvents.length + store.pendingUndos.length > 0) scheduleFlush();
}

/** Remise à zéro de l'état de module — réservée aux tests. */
export function __resetMatchSyncFlushState(): void {
  if (flushTimeout) clearTimeout(flushTimeout);
  flushTimeout = null;
  inFlightFlush = null;
  mountedInstances = 0;
  refusedSinceDismiss.clear();
  undoRefusedSinceDismiss.clear();
  heldRefused.clear();
  heldDismissed.clear();
  undoStuckNotified.clear();
  isolatedMatches.clear();
  deviceConflictNotified.clear();
  liveScreenMounted = false;
}

export function useMatchSyncFlush(): MatchSyncFlush {
  /**
   * Le timer est partagé : le démontage d'une instance ne doit pas annuler un
   * flush programmé alors que d'autres instances vivent encore — sinon quitter
   * un widget en cours de match gèlerait la synchro des autres. On compte donc
   * les instances et on n'annule qu'au démontage de la dernière, moment où plus
   * personne ne saisit. Les événements déjà en file survivent de toute façon :
   * ils sont persistés dans `useSyncStore` et le prochain montage les
   * reprogramme (effet plus bas), donc rien n'est perdu.
   */
  useEffect(() => {
    mountedInstances += 1;
    return () => {
      mountedInstances -= 1;
      if (mountedInstances <= 0) {
        mountedInstances = 0;
        if (flushTimeout) {
          clearTimeout(flushTimeout);
          flushTimeout = null;
        }
      }
    };
  }, []);

  useEffect(() => {
    // Same rule as a return to the foreground. Status alone is not enough: a queue
    // reloaded while offline stays `idle` until a send fails, and nothing else
    // would plan its send once the network is back (VOL-14).
    const handleOnline = () => resumeSyncOnForeground();
    const handleOffline = () => {
      useSyncStore.getState().setStatus('offline');
    };
    window.addEventListener('online', handleOnline);
    window.addEventListener('offline', handleOffline);
    return () => {
      window.removeEventListener('online', handleOnline);
      window.removeEventListener('offline', handleOffline);
    };
  }, []);

  const pendingEvents = useSyncStore((state) => state.pendingEvents);
  const pendingUndos = useSyncStore((state) => state.pendingUndos);
  const status = useSyncStore((state) => state.status);

  useEffect(() => {
    if (pendingEvents.length + pendingUndos.length > 0 && status === 'idle' && navigator.onLine) {
      scheduleFlush();
    }
  }, [pendingEvents.length, pendingUndos.length, status]);

  return { queueEvent, flushPendingEvents };
}
